import { Link, createFileRoute } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'

import {
  getGetAdminCalendarQueryKey,
  useConfirmBooking,
  useGetAdminCalendar,
  useMarkBookingPaid,
  useMarkNoShow,
} from '../../../api/generated/endpoints/admin/admin'
import type { CalendarBooking } from '../../../api/generated/model'
import { KIND_LABELS, KIND_STYLES, toMinutes, SIN_NOMBRE } from '../../../lib/admin-calendar'
import { formatUYU } from '../../../lib/money'
import { nowAtFacility, todayAtFacility } from '../../../lib/time'

/**
 * The screen he opens most, ordered by "what do I need in the next minute".
 *
 * Uses the calendar endpoint with from = to = today rather than a second
 * endpoint: one response model, one cache key, one thing to keep correct.
 */
export const Route = createFileRoute('/_authed/_admin/admin/today')({
  component: TodayPage,
})

function TodayPage() {
  const today = todayAtFacility()
  const now = nowAtFacility()

  const calendar = useGetAdminCalendar(
    { from: today, to: today },
    {
      query: {
        // The iPad sits open on the counter all day.
        staleTime: 30_000,
        refetchInterval: 60_000,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
      },
    },
  )

  if (calendar.isPending) return <p className="text-xl">Cargando el día…</p>
  if (calendar.isError) {
    return (
      <p role="alert" className="text-xl text-red-700">
        No se pudo cargar el día.
      </p>
    )
  }

  const bookings = [...calendar.data.bookings].sort((a, b) => a.start.localeCompare(b.start))
  const minutesNow = toMinutes(now)

  const inProgress = calendar.data.bays.map((bay) => ({
    bay,
    booking: bookings.find(
      (b) =>
        b.bay_id === bay.id && toMinutes(b.start) <= minutesNow && toMinutes(b.end) > minutesNow,
    ),
  }))
  const next = bookings.find((b) => toMinutes(b.start) > minutesNow)
  const past = bookings.filter((b) => toMinutes(b.end) <= minutesNow)
  const upcoming = bookings.filter((b) => toMinutes(b.end) > minutesNow)

  return (
    <div className="space-y-8">
      <h1 className="text-3xl font-semibold">Hoy</h1>

      {/* What is in each bay this minute -- the thing to glance at while
          somebody is standing in front of you. */}
      <section className="rounded-lg bg-slate-900 px-6 py-5 text-white">
        <h2 className="text-sm font-semibold uppercase text-slate-300">Ahora</h2>
        <ul className="mt-3 space-y-2">
          {inProgress.map(({ bay, booking }) => (
            <li key={bay.id} className="text-xl">
              <span className="font-semibold">{bay.name}</span>
              {' — '}
              {booking ? (
                <>
                  {booking.customer_name ?? booking.admin_note ?? SIN_NOMBRE}, hasta{' '}
                  <span className="tabular-nums">{booking.end}</span>
                  {booking.customer_phone && (
                    <span className="ml-2 tabular-nums text-slate-300">
                      {booking.customer_phone}
                    </span>
                  )}
                </>
              ) : (
                <span className="text-slate-300">libre</span>
              )}
            </li>
          ))}
        </ul>
        {next && (
          <p className="mt-4 border-t border-slate-700 pt-3 text-lg text-slate-300">
            Próximo: <span className="tabular-nums text-white">{next.start}</span> · {next.bay_name}{' '}
            · {next.customer_name ?? next.admin_note ?? SIN_NOMBRE}
          </p>
        )}
      </section>

      {bookings.length === 0 ? (
        <div className="rounded-lg border-2 border-slate-300 bg-white p-8 text-center">
          <p className="text-xl">Hoy no hay reservas.</p>
        </div>
      ) : (
        <>
          <ul className="space-y-3">
            {upcoming.map((booking) => (
              <TodayCard key={booking.id} booking={booking} minutesNow={minutesNow} />
            ))}
          </ul>

          {past.length > 0 && (
            <>
              <p className="border-t-2 border-slate-300 pt-3 text-base uppercase text-slate-700">
                Más temprano
              </p>
              <ul className="space-y-3">
                {past.map((booking) => (
                  <TodayCard key={booking.id} booking={booking} minutesNow={minutesNow} />
                ))}
              </ul>
            </>
          )}
        </>
      )}

      <Link
        to="/admin/calendar"
        search={{ date: today }}
        className="flex min-h-20 w-full items-center justify-center rounded-lg bg-slate-900 px-6 text-2xl font-semibold text-white focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
      >
        + Agregar algo al calendario
      </Link>
    </div>
  )
}

function TodayCard({ booking, minutesNow }: { booking: CalendarBooking; minutesNow: number }) {
  const queryClient = useQueryClient()
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: getGetAdminCalendarQueryKey() })

  const confirm = useConfirmBooking({ mutation: { onSuccess: invalidate } })
  const noShow = useMarkNoShow({ mutation: { onSuccess: invalidate } })
  const markPaid = useMarkBookingPaid({ mutation: { onSuccess: invalidate } })

  const startedAWhileAgo = toMinutes(booking.start) + 15 <= minutesNow
  const isPast = toMinutes(booking.end) <= minutesNow
  // `settled`, NOT `paid`. A package hour keeps its full price on the row --
  // credit_service.was_spent_on says so in as many words -- so asking `paid`
  // would print "Debe $ 600" on an hour somebody already bought and offer to
  // take the money a second time.
  const owes = !booking.settled && booking.price_uyu_pesos > 0 && booking.kind !== 'block'

  // At most ONE inline action per card, and only when there is an obviously-now
  // thing to do. Everything else lives on the booking's own screen.
  //
  // Which one is decided by where the session sits in the day, so the two never
  // compete: while it is running and nobody has turned up, the question is
  // whether they are coming; once it is over, the question is the money.
  //
  // Closing the no-show window at the end of the hour is only safe because the
  // booking's own screen offers it with no time limit. Without that it would be
  // a 45-minute window to record an absence, after which the fact became
  // unrecordable -- and once the nightly job starts completing past sessions,
  // unrecordable for good.
  const action =
    booking.status === 'pending_payment'
      ? ('confirm' as const)
      : !isPast && startedAWhileAgo && booking.status === 'confirmed'
        ? ('no-show' as const)
        : isPast && owes
          ? ('paid' as const)
          : null

  return (
    <li
      className={[
        'rounded-lg border-2 p-5',
        KIND_STYLES[booking.kind] ?? 'bg-white border-slate-300',
        isPast ? 'text-slate-600' : '',
      ].join(' ')}
    >
      <div className="flex flex-wrap items-baseline gap-x-4">
        <span className="text-2xl font-semibold tabular-nums">
          {booking.start} &rarr; {booking.end}
        </span>
        <span className="text-base font-semibold uppercase">{booking.bay_name}</span>
        <span className="text-base font-semibold uppercase">
          {KIND_LABELS[booking.kind] ?? booking.kind}
        </span>
      </div>

      <p className="mt-1 text-xl font-medium">
        {booking.customer_name ?? booking.admin_note ?? SIN_NOMBRE}
      </p>
      {booking.coach_name && <p className="text-base">con {booking.coach_name}</p>}

      <div className="mt-1 flex flex-wrap items-center justify-between gap-4">
        {booking.customer_phone ? (
          <a
            href={`tel:${booking.customer_phone}`}
            className="inline-flex min-h-11 items-center text-lg tabular-nums underline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
          >
            {booking.customer_phone}
          </a>
        ) : (
          <span />
        )}
        {booking.paid ? (
          <span className="text-lg font-medium text-green-800">
            Pago · {formatUYU(booking.paid_amount_uyu_pesos ?? booking.price_uyu_pesos)}
          </span>
        ) : (
          owes && <span className="text-lg">Debe {formatUYU(booking.price_uyu_pesos)}</span>
        )}
      </div>

      {action === 'confirm' && (
        <button
          type="button"
          onClick={() => confirm.mutate({ bookingId: booking.id })}
          disabled={confirm.isPending}
          className="mt-4 min-h-16 rounded-md bg-slate-900 px-8 text-xl text-white focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900 disabled:bg-slate-400"
        >
          {confirm.isPending ? 'Confirmando…' : 'Confirmar'}
        </button>
      )}
      {action === 'paid' && (
        <button
          type="button"
          // Efectivo, because that is what he is handed nine times out of ten
          // and this is the one-tap surface. The booking's own screen changes
          // the method or the amount afterwards.
          onClick={() => markPaid.mutate({ bookingId: booking.id, data: { method: 'efectivo' } })}
          disabled={markPaid.isPending}
          className="mt-4 min-h-16 rounded-md bg-slate-900 px-8 text-xl text-white focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900 disabled:bg-slate-400"
        >
          {markPaid.isPending ? 'Cobrando…' : 'Cobré en efectivo'}
        </button>
      )}
      {action === 'no-show' && (
        <button
          type="button"
          onClick={() => noShow.mutate({ bookingId: booking.id })}
          disabled={noShow.isPending}
          className="mt-4 min-h-16 rounded-md border-2 border-slate-400 bg-white px-8 text-xl focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900 disabled:text-slate-500"
        >
          {noShow.isPending ? 'Marcando…' : 'No se presentó'}
        </button>
      )}
    </li>
  )
}
